/**
 * useTeacherMode.js
 * ----------------------------------------------------------------
 * Hook qui gère le "Mode Teacher Public" — activé quand un
 * enseignant navigue le site public via l'URL `?teacher=1`.
 *
 * Comportement :
 *  1. Détecte `?teacher=1` dans l'URL au montage.
 *  2. Persiste `teacherMode=1` en sessionStorage (survit à la navigation).
 *  3. Nettoie l'URL (retire `?teacher=1`) pour ne pas polluer.
 *  4. Expose `teacherMode` (bool) et `disableTeacherMode()`.
 *
 * Le mode expire :
 *  - à la fermeture de l'onglet (sessionStorage éphémère)
 *  - OU quand l'enseignant clique sur le badge "Quitter le mode"
 * ---------------------------------------------------------------- */
import { useEffect, useState, useCallback } from 'react';
import { useApp } from '../contexts/AppContext';

const STORAGE_KEY = 'c2r_teacher_mode';

export function useTeacherMode() {
  const { user } = useApp() || {};
  const [teacherMode, setTeacherMode] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.sessionStorage.getItem(STORAGE_KEY) === '1';
  });

  // Détection ?teacher=1 dans l'URL → activation + nettoyage
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('teacher') === '1') {
      window.sessionStorage.setItem(STORAGE_KEY, '1');
      setTeacherMode(true);
      // Nettoie l'URL (retire teacher=1) sans recharger
      params.delete('teacher');
      const clean = window.location.pathname + (params.toString() ? `?${params}` : '') + window.location.hash;
      window.history.replaceState({}, '', clean);
    }
  }, []);

  const disableTeacherMode = useCallback(() => {
    if (typeof window === 'undefined') return;
    window.sessionStorage.removeItem(STORAGE_KEY);
    setTeacherMode(false);
  }, []);

  // Un enseignant connecté est TOUJOURS en teacher mode (implicite)
  const role = user?.role;
  const isConnectedTeacher = role === 'enseignant' || role === 'admin' || user?.is_admin;

  return {
    /**
     * true si l'utilisateur peut voir les outils enseignant sur le site public.
     * (connecté en tant qu'enseignant OU jeton `?teacher=1` actif)
     */
    teacherMode: isConnectedTeacher || teacherMode,
    /** true si activé via `?teacher=1` (permet d'afficher le badge de sortie) */
    isTeacherModeToken: teacherMode && !isConnectedTeacher,
    disableTeacherMode,
  };
}

export default useTeacherMode;
